import { FC, useState } from 'react';

import { useGlobalMediaStore } from '../../../../../modules/bonus-feed/hooks/use-global-media-store';
import { useMediaOrchestration } from '../../../../../modules/bonus-feed/hooks/use-media-orchestration';
import { Bonus } from '../../../../../types/bonus';
import { CustomVideoPlayer } from '../../../../video/custom-video-player';
import { VideoSourceElements } from './components/video-source-elements';
import { useGlobalVolume } from './hooks/use-global-volume';
import useVideoTracking from './hooks/use-video-tracking';

type VideoAttachmentProps = {
	bonus: Bonus;
	focused?: boolean;
};

export const VideoAttachment: FC<VideoAttachmentProps> = ({ bonus, focused = false }) => {
	const videoAttachment = bonus.attachments[0];
	const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
	const { playVideo, pauseVideo } = useGlobalMediaStore();

	// Global volume for the custom video player which is called explicitly
	const { getVolume, setVolume } = useGlobalVolume();

	// Adds tracking on the video playing behavior
	useVideoTracking(videoRef, videoAttachment.video_id);

	// Handle viewport-based video playback
	useMediaOrchestration(videoRef, {
		bonusId: bonus.id,
		focused,
	});

	// Captions are hardcoded to English for now but we will iterate on them later
	const captions = videoAttachment.video_transcript_url ? [{ src: videoAttachment.video_transcript_url, label: 'English', srcLang: 'en' }] : undefined;

	return (
		<CustomVideoPlayer
			videoId={videoAttachment.video_id}
			title={videoAttachment.alt_text}
			poster={videoAttachment.video_poster_url}
			ref={setVideoRef}
			playVideo={playVideo}
			getGlobalVolume={getVolume}
			setGlobalVolume={setVolume}
			pauseVideo={pauseVideo}
			condenseVideoControlsForMobile={true}
			defaultMuted={true}
			captions={captions}
		>
			<VideoSourceElements
				videoRef={videoRef}
				videoHdSource={String(videoAttachment.video_hd_url)}
				videoSdSource={String(videoAttachment.video_sd_url)}
				videoOriginalSource={String(videoAttachment.video_original_url)}
				videoTranscriptSource={String(videoAttachment.video_transcript_url)}
			/>
		</CustomVideoPlayer>
	);
};
